/**
 * Onboard Entry Route (Step 1: Account Creation)
 *
 * Public entry point for the onboarding flow. Handles account creation
 * (email + password + confirm password), then redirects to the business-info step.
 * Existing users are directed to /auth/sign-in via a link in the UI.
 *
 * @see {@link ./__tests__/entry.test.tsx}
 */

import { data, redirect } from 'react-router'
import type { LoaderFunctionArgs, ActionFunctionArgs } from 'react-router'
import { useActionData, useLoaderData, useNavigation } from 'react-router'
import Csrf from 'csrf'

import { createAuthClient } from './lib/loader-utils'
import { createAccount } from './lib/actions.auth'
import { EntryChoice } from './components/entry-choice'
import { EntryAuthenticated } from './components/entry-authenticated'
import { parseCsrfSecretCookie, serializeCsrfSecretCookie } from '~/lib/server/cookies/csrf-secret.cookie'

const csrf = new Csrf()

/**
 * Loader: Check if user is already authenticated.
 * Authenticated users see their account info with a Continue button.
 * Unauthenticated users see the signup form.
 */
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { supabase } = createAuthClient(request)

  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (user) {
    return { isAuthenticated: true, userEmail: user.email ?? '', csrfToken: '' }
  }

  // Generate CSRF token for the sign-up form
  const secret = csrf.secretSync()
  const csrfToken = csrf.create(secret)
  const csrfCookie = await serializeCsrfSecretCookie(secret)

  return data({ isAuthenticated: false, userEmail: '', csrfToken }, { headers: { 'Set-Cookie': csrfCookie } })
}

/**
 * Action: Handle create account form submission.
 */
export const action = async ({ request }: ActionFunctionArgs) => {
  const { supabase, headers } = createAuthClient(request)
  const formData = await request.formData()

  const rawEmail = (formData.get('email') as string)?.toLowerCase().trim() || ''

  // CSRF validation
  const csrfToken = formData.get('csrfToken') as string
  const csrfSecret = await parseCsrfSecretCookie(request)
  if (!csrfSecret || !csrfToken || !csrf.verify(csrfSecret, csrfToken)) {
    return data({ success: false, error: 'Invalid form submission. Please refresh and try again.' }, { status: 403 })
  }

  // Honeypot check — if the hidden field has a value, it's likely a bot
  const honeypot = formData.get('website') as string
  if (honeypot) {
    return data({ success: false, error: 'Something went wrong. Please try again.' }, { status: 400 })
  }

  if (!rawEmail) {
    return data({ success: false, error: 'Email is required' }, { status: 400 })
  }

  const password = formData.get('password') as string | null
  const confirmPassword = formData.get('confirmPassword') as string | null

  if (!password || password.length < 8) {
    return data({ success: false, error: 'Password must be at least 8 characters' }, { status: 400 })
  }

  if (password !== confirmPassword) {
    return data({ success: false, error: 'Passwords do not match' }, { status: 400 })
  }

  const result = await createAccount({ supabase, email: rawEmail, password, headers, request })

  if (!result.success) {
    return data({ success: false, error: result.error }, { status: 400 })
  }

  return redirect(result.redirect ?? '/onboard/business-info', { headers })
}

/**
 * Entry page component for the onboarding flow.
 * Shows the signup form for unauthenticated users, or account info
 * with a Continue button for authenticated users.
 */
export default function OnboardEntry(): JSX.Element {
  const { isAuthenticated, userEmail, csrfToken } = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const isSubmitting = navigation.state === 'submitting'

  if (isAuthenticated) {
    return (
      <div className="flex items-center justify-center p-4">
        <EntryAuthenticated userEmail={userEmail} />
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center p-4">
      <EntryChoice actionError={actionData?.error} isSubmitting={isSubmitting} csrfToken={csrfToken} />
    </div>
  )
}
